import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';

export type FeatureConfig = Record<string, Record<string, boolean | string | number>>;

export interface TenantConfig {
  id: string;
  name: string;
  code: string;
  gamEnabled: boolean;
  gamNetworkId: string | null;
  // Tenant-level feature flags, sectioned by game type, e.g.
  // { pickem: { enableFullNavigation: true } }.
  featureConfig?: FeatureConfig | null;
  createdAt: string;
  updatedAt: string;
}

export type GoogleAdsPlacementKey =
  | 'TOP_PAGE'
  | 'BELOW_HEADER'
  | 'BODY'
  | 'PICKS_AFTER_2'
  | 'PICKS_AFTER_4'
  | 'PICKEM_CONTEST_CARD'
  | 'PICKEM_STICKY_FOOTER'
  | 'POST_ENTRY';

export type AdSize = [number, number];

export interface ResponsiveSizeMapping {
  viewport: [number, number];
  sizes: AdSize[] | [];
}

export interface GoogleAdsPlacement {
  placementKey: GoogleAdsPlacementKey;
  enabled: boolean;
  adUnitPath?: string | null;
  sizes?: AdSize[] | null;
  sizeMappings?: ResponsiveSizeMapping[] | null;
  divIdPrefix?: string | null;
  targeting?: Record<string, string> | null;
}

export type GoogleAdsPlacements = Record<GoogleAdsPlacementKey, GoogleAdsPlacement>;

export const useGetTenantGoogleAdsPlacements = (slug: string) => {
  return useQuery({
    queryKey: ['tenant', slug, 'google-ads-placements'],
    queryFn: async (): Promise<GoogleAdsPlacement[]> => {
      const response = await apiClient.get('/api/tenants/{slug}/google-ads-placements' as any, {
        pathParams: { slug },
      });
      return (response as any).placements || [];
    },
    enabled: !!slug,
    staleTime: 1000 * 60 * 5,
    retry: 3,
  });
};

/**
 * Hook to fetch tenant configuration by slug
 * @param slug - The tenant slug (e.g., 'delta', 'draftkings')
 */
export const useGetTenant = (slug: string) => {
  return useQuery({
    queryKey: ['tenant', slug],
    queryFn: async (): Promise<TenantConfig> => {
      const response = await apiClient.get('/api/tenants/{slug}', {
        pathParams: { slug },
      });
      return (response as any).tenant;
    },
    enabled: !!slug,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    retry: 3,
  });
};

/**
 * Hook to fetch a pickem-specific Google ad placement, addressed by placement
 * key. Per-pickem rows are key-addressable (contest card, sticky footer, …);
 * defaults to the contest card so existing callers are unaffected.
 * @param pickemId - The pickem ID
 * @param placementKey - Which per-pickem placement to fetch
 */
export const useGetPickemGoogleAd = (
  pickemId: string | null | undefined,
  placementKey: GoogleAdsPlacementKey = 'PICKEM_CONTEST_CARD'
) => {
  return useQuery({
    queryKey: ['pickem', pickemId, 'google-ad', placementKey],
    queryFn: async (): Promise<GoogleAdsPlacement | null> => {
      if (!pickemId) return null;
      const response = await apiClient.get('/api/pickems/{id}/google-ad' as any, {
        pathParams: { id: pickemId },
        params: { placementKey } as any,
      });
      return (response as any).placement || null;
    },
    enabled: !!pickemId,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    retry: 3,
  });
};

/**
 * Extract Google Ad Manager configuration from tenant
 */
export const getGAMConfigFromTenant = (tenant: TenantConfig | undefined) => {
  if (!tenant || !tenant.gamEnabled) {
    return null;
  }

  return {
    networkId: tenant.gamNetworkId || '',
    enabled: tenant.gamEnabled,
  };
};
